import { ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useState } from "react";

import {
  isBeforeDay,
  isDateAllowed,
  parseDateInputValue,
  toDateInputValue,
} from "../utils/deliveryDates";

const weekdayLabels = Array.from({ length: 7 }, (_, index) =>
  new Intl.DateTimeFormat("en-AU", { weekday: "short" }).format(new Date(2024, 0, 7 + index))
);

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date, amount) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function buildMonthDays(monthDate) {
  const firstDay = startOfMonth(monthDate);
  const daysInMonth = new Date(firstDay.getFullYear(), firstDay.getMonth() + 1, 0).getDate();
  const blanks = Array.from({ length: firstDay.getDay() }, () => null);
  const days = Array.from(
    { length: daysInMonth },
    (_, index) => new Date(firstDay.getFullYear(), firstDay.getMonth(), index + 1)
  );

  return [...blanks, ...days];
}

export function DeliveryDateCalendar({ minDate, onChange, value }) {
  const selectedDate = parseDateInputValue(value);
  const initialMonth = selectedDate || minDate || new Date();
  const [visibleMonth, setVisibleMonth] = useState(startOfMonth(initialMonth));
  const monthDays = useMemo(() => buildMonthDays(visibleMonth), [visibleMonth]);
  const selectedValue = selectedDate ? toDateInputValue(selectedDate) : "";
  const monthLabel = new Intl.DateTimeFormat("en-AU", {
    month: "long",
    year: "numeric",
  }).format(visibleMonth);

  return (
    <div className="rounded-2xl border border-border bg-background/90 p-3 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <button
          aria-label="Previous month"
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border text-muted-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
          disabled={addMonths(visibleMonth, -1) < startOfMonth(minDate)}
          onClick={() => setVisibleMonth((current) => addMonths(current, -1))}
          type="button"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <p className="text-sm font-semibold">{monthLabel}</p>
        <button
          aria-label="Next month"
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border text-muted-foreground transition hover:bg-secondary"
          onClick={() => setVisibleMonth((current) => addMonths(current, 1))}
          type="button"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {weekdayLabels.map((label) => (
          <div className="py-1 text-xs font-semibold text-muted-foreground" key={label}>
            {label}
          </div>
        ))}

        {monthDays.map((date, index) => {
          if (!date) {
            return <div aria-hidden="true" className="aspect-square" key={`blank-${index}`} />;
          }

          const dateValue = toDateInputValue(date);
          const disabled = isBeforeDay(date, minDate) || !isDateAllowed(date);
          const selected = selectedValue === dateValue;

          return (
            <button
              aria-pressed={selected}
              className={[
                "aspect-square rounded-xl text-sm font-semibold transition",
                selected ? "bg-primary text-primary-foreground shadow-sm" : "text-foreground hover:bg-secondary",
                disabled ? "cursor-not-allowed bg-muted/50 text-muted-foreground opacity-35 hover:bg-muted/50" : "",
              ].join(" ")}
              disabled={disabled}
              key={dateValue}
              onClick={() => onChange(dateValue)}
              type="button"
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
